import React, { createContext, useContext, useState } from 'react'
import { AxiosRequestConfig } from 'axios'

import { removePlusSign } from 'utils/functions/formatNumber'
import { postSubscriber, resendEmail, findInOb2Sib, getSubscriber, postUpdatePassword, postAdministrator, postSendEmailRecoveryPassword } from 'services/api'
import countriesMock from 'services/mock/countries.json'
import { IFetchedfindInOb2Sib, IFindInObSib, IResendEmail, IResponseGeneric, ISendEmailRecoveryPassword, ISuscriberGet, IUpdatePassword } from 'services/IApi.d'
import { customURIEncode } from 'utils/functions/encodePassword'
import { ISubscriberData, IBreadcrumbItem, ISubscriberSave, ISubscriberProvider, ICountries } from './ISubscriberProvider.d'

const SubscriberContext = createContext(null)
interface SubscriberProviderProps {
  children: React.ReactNode;
  initData: Record<string, unknown>;
}

function SubscriberProvider({ children, initData }: Readonly<SubscriberProviderProps>) {
  const [breadcrumbItems, setBreadcrumbItems] = useState<IBreadcrumbItem[]>([])
  const [subscriberData, setSubscriberData] = useState<ISubscriberData>(null)
  const [flow, setFlow] = useState<'suscriptor' | 'administrator' | 'legalRepresentative'>('suscriptor')
  const [adminCollaboratorData, setAdminCollaboratorData] = useState({})
  const [collaboratorId, setCollaboratorId] = useState<string>('')
  const countries: ICountries[] = countriesMock.data.paisList.filter(pais => pais.paisCodTel)
  const [codeUser, setCodeUser] = useState<string>(undefined)

  const saveData = async (fields: ISubscriberData, recaptchaToken: string) => {
    const isAdminFlow = flow === 'administrator'
    const data: ISubscriberSave = {
      nombre: fields.name,
      codNacionalidad: isAdminFlow ? fields.nacionalidad : fields.areaCode,
      apellido: fields.lastName,
      email: fields.email,
      telefono: {
        codArea: fields.codArea,
        codPais: isAdminFlow ? removePlusSign(fields.codPais) : removePlusSign(fields.countryPhoneCode),
        numero: Number(removePlusSign(fields.phone)),
      },
      documento: {
        nacionalidad: fields.nacionality,
        // eslint-disable-next-line no-nested-ternary
        numero: isAdminFlow
          ? fields.document.trim()
          : (fields.documentType === 'cuil'
            ? fields.document.trim()
            : fields.documentForeign.trim()),
        tipo: fields.documentType === 'Pasaporte' ? 'PAS' : 'Documento',
      },
      credenciales: {
        password: customURIEncode(fields.password),
        usuario: fields.user,
      },
      collaboratorId: isAdminFlow ? fields.collaboratorId : ''
    }

    const options: AxiosRequestConfig<unknown> = {
      headers: {
        'x-recaptcha-token': recaptchaToken,
      }
    }

    const createdSubscriber = isAdminFlow ? await postAdministrator(data, options) : await postSubscriber(data, options)
    return createdSubscriber
  }

  const resendValidationEmail = async (destinoNotificacion: string, requestId: string, recaptchaToken: string, tipoMensaje: string = 'REGISTRO_SUSCRIPTOR', pais: string = '', nacionalidad: string = '') => {
    const data: IResendEmail = {
      canal: 'web',
      destinoNotificacion,
      fechaHoraSolicitud: new Date().toJSON().slice(0, 22),
      parametrosAdicionales: (pais !== '' && nacionalidad !== '') ? `pais=${pais}&nacionalidad=${nacionalidad}&email=${destinoNotificacion}` : '',
      requestId,
      tipoMensaje,
      tipoNotificacion: 'EMAIL',
    }
    const response = await resendEmail(data, { headers: { 'x-recaptcha-token': recaptchaToken } })
    return response
  }

  const sendValidationEmail = async (destinoNotificacion: string, requestId: string, parametrosAdicionales: unknown, recaptchaToken: string, tipoMensaje: string = 'REGISTRO_SUSCRIPTOR') => {
    const data: IResendEmail = {
      canal: 'web',
      destinoNotificacion,
      fechaHoraSolicitud: new Date().toJSON().slice(0, 22),
      parametrosAdicionales: Object.keys(parametrosAdicionales).map(key =>
        parametrosAdicionales[key] ? (`${key}=${parametrosAdicionales[key]}`) :
          '').filter(key => key !== '').join('&'),
      requestId,
      tipoMensaje,
      tipoNotificacion: 'EMAIL',
    }
    const response = await resendEmail(data, { headers: { 'x-recaptcha-token': recaptchaToken } })
    return response
  }

  const sendEmailRecoveryPassword = async (documentType: string, documentNumber: string, recaptchaToken: string, documentNationality: string = '') => {
    const data: ISendEmailRecoveryPassword = {
      documentType,
      documentNationality,
      documentNumber
    }
    const response = await postSendEmailRecoveryPassword(data, { headers: { 'x-recaptcha-token': recaptchaToken } })
    return response
  }

  const updatePassword = async fields => {
    const data: IUpdatePassword = {
      password: customURIEncode(fields.password),
      document: fields.documento,
      nationality: fields.nacionalidad,
      country: fields.pais,
      user: fields.user,
      idSuscriptor: fields.idSuscriptor
    }
    const response = await postUpdatePassword(data)
    return response
  }

  const addBreadcrumbItem = (breadcrumbItem: IBreadcrumbItem): void => {
    setBreadcrumbItems(prev => {
      const previousItem = prev.map(item => ({ ...item, isCurrentPage: false }))
      return [...previousItem, breadcrumbItem]
    })
  }

  const validateInSIB = async (suscriber: IFindInObSib, recaptchaToken: string):
    Promise<IFetchedfindInOb2Sib> => {
    const response = await findInOb2Sib(suscriber, recaptchaToken)
    return response
  }

  const getSubscriberInfo = async (suscriber: ISuscriberGet):
    Promise<IResponseGeneric> => {
    const response = await getSubscriber(suscriber)
    return response
  }
  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const store: ISubscriberProvider = {
    initData,
    breadcrumbItems,
    addBreadcrumbItem,
    subscriberData,
    setSubscriberData,
    countries,
    saveData,
    resendValidationEmail,
    sendEmailRecoveryPassword,
    validateInSIB,
    getSubscriberInfo,
    updatePassword,
    adminCollaboratorData,
    setAdminCollaboratorData,
    setCollaboratorId,
    collaboratorId,
    flow,
    setFlow,
    sendValidationEmail,
    codeUser,
    setCodeUser,
  }
  return <SubscriberContext.Provider value={store}>{children}</SubscriberContext.Provider>
}
const useSubscriberContext = (): ISubscriberProvider => {
  const context: ISubscriberProvider = useContext(SubscriberContext)
  if (context === undefined) { throw new Error('SubscriberContext must be used within a SubscriberProvider') }
  return context
}
export { SubscriberProvider, useSubscriberContext }